import type {
  ThriveInputEvent,
  ThriveInputEventOf,
  ThriveInputEventType,
} from '../../shared/types/input-events';

export type Unsubscribe = () => void;

export type SemanticEventListener<TType extends ThriveInputEventType = ThriveInputEventType> = (
  event: ThriveInputEventOf<TType>,
) => void;

export type SemanticEventAnyListener = (event: ThriveInputEvent) => void;

/**
 * The single channel between input adapters (keyboard, pose) and everything downstream.
 * Adapters emit; the cooperative manager, mission runtime and scenes subscribe.
 */
export interface SemanticEventBus {
  subscribe<TType extends ThriveInputEventType>(
    type: TType,
    listener: SemanticEventListener<TType>,
  ): Unsubscribe;

  subscribeAll(listener: SemanticEventAnyListener): Unsubscribe;

  unsubscribe<TType extends ThriveInputEventType>(
    type: TType,
    listener: SemanticEventListener<TType>,
  ): void;

  emit(event: ThriveInputEvent): void;

  /** Drops every listener. Used on session teardown. */
  clear(): void;

  listenerCount(type?: ThriveInputEventType): number;
}

type AnyListener = (event: never) => void;

export class InMemorySemanticEventBus implements SemanticEventBus {
  readonly #byType = new Map<ThriveInputEventType, Set<AnyListener>>();
  readonly #all = new Set<SemanticEventAnyListener>();

  subscribe<TType extends ThriveInputEventType>(
    type: TType,
    listener: SemanticEventListener<TType>,
  ): Unsubscribe {
    let listeners = this.#byType.get(type);
    if (!listeners) {
      listeners = new Set();
      this.#byType.set(type, listeners);
    }
    listeners.add(listener as AnyListener);
    return () => {
      this.unsubscribe(type, listener);
    };
  }

  subscribeAll(listener: SemanticEventAnyListener): Unsubscribe {
    this.#all.add(listener);
    return () => {
      this.#all.delete(listener);
    };
  }

  unsubscribe<TType extends ThriveInputEventType>(
    type: TType,
    listener: SemanticEventListener<TType>,
  ): void {
    const listeners = this.#byType.get(type);
    if (!listeners) return;
    listeners.delete(listener as AnyListener);
    if (listeners.size === 0) this.#byType.delete(type);
  }

  emit(event: ThriveInputEvent): void {
    // Snapshot so a listener added during dispatch does not receive the in-flight event,
    // and re-check membership so a listener torn down mid-dispatch is not called.
    const typed = this.#byType.get(event.type);
    if (typed) {
      for (const listener of [...typed]) {
        if (!typed.has(listener)) continue;
        (listener as (e: ThriveInputEvent) => void)(event);
      }
    }
    for (const listener of [...this.#all]) {
      if (!this.#all.has(listener)) continue;
      listener(event);
    }
  }

  clear(): void {
    this.#byType.clear();
    this.#all.clear();
  }

  listenerCount(type?: ThriveInputEventType): number {
    if (type === undefined) {
      let total = this.#all.size;
      for (const listeners of this.#byType.values()) total += listeners.size;
      return total;
    }
    return this.#byType.get(type)?.size ?? 0;
  }
}

export function createSemanticEventBus(): SemanticEventBus {
  return new InMemorySemanticEventBus();
}
